import React from "react";
import * as Yup from "yup";
import { observer } from "mobx-react";
import Http from "services/Http";
import {
  Row,
  Col,
  Panel,
  DataTableColumn,
  DataTable,
  Form,
  TextBox,
  Button,
  FormState,
  Select,
} from "@countysuite/ui";
import { getInputValueByName } from "utils/getInputValueByName";
import rules, { RuleDefinition } from "pages/SystemSettings/Rules/rules";
import { BasePage } from "pages/BasePage";
import SelectTag from "components/SelectTag/SelectTag";
import { DropdownCategory } from "types/DropdownCategory";

interface Props {}
interface State {
  selectedRule: RuleDefinition;
  newValue: string;
  isDirty: boolean;
  possibleCategories: DropdownCategory[];
  descriptionLkp: DescriptionHashTable;
}

interface DescriptionHashTable {
  [key: string]: string;
}

function emptyRuleDefinition(): RuleDefinition {
  return {
    id: 0,
    description: "",
    name: "",
    ruleValues: [],
    tableName: "",
  };
}

@observer
export class RuleSettingsPage extends React.Component<Props, State> {
  state: State = {
    selectedRule: emptyRuleDefinition(),
    newValue: "",
    isDirty: false,
    possibleCategories: [],
    descriptionLkp: {},
  };

  componentDidMount() {
    this.fetchRuleDefinitions();
  }

  fetchRuleDefinitions = async () => {
    const res = await Http.get<any>({
      url: "SystemSettings/GetRules",
      waitMessage: "Fetching rule settings, please wait...",
    });

    if (res.success) {
      rules.load(res.ruleDefinitions);
    }
  };

  postRuleDefinition = async () => {
    const { selectedRule } = this.state;

    if (!selectedRule || !selectedRule.id) {
      alert("Please select a rule definition!");
    }

    await Http.post<any>({
      url: "SystemSettings/UpdateRuleDefinition",
      data: {
        ruleDefinitionID: selectedRule.id,
        ruleValues: selectedRule.ruleValues || [],
      },
      waitMessage: `Saving ${selectedRule.name}, please wait.`,
    });

    await this.fetchRuleDefinitions();

    this.setState({
      selectedRule: emptyRuleDefinition(),
      isDirty: false,
      possibleCategories: [],
      descriptionLkp: {},
    });
  };

  addRuleValue = (val: string) => {
    if (!val || !Number(val) || Number(val) <= 0) {
      return;
    }
    //TODO: flatten state, to increase readability.
    this.setState((prevState) => {
      return {
        isDirty: true,
        newValue: "0",
        selectedRule: {
          ...prevState.selectedRule,
          ruleValues: [...prevState.selectedRule.ruleValues, val],
        },
      };
    });
  };

  removeRuleValue = (valToRemove: string) => {
    if (!valToRemove) return;
    //TODO: setState call is needlessly complex because the state is nested too deeply.
    //      flatten the state and/or split components up into different concerns.
    this.setState((prevState) => {
      let { ruleValues } = prevState.selectedRule;
      const newRuleValues = ruleValues.filter((rv) => rv !== valToRemove);

      return {
        isDirty: true,
        selectedRule: {
          ...prevState.selectedRule,
          ruleValues: newRuleValues,
        },
      };
    });
  };

  getCategoryDescription = (val: string) => {
    const desc = this.state.descriptionLkp[val];
    if (desc) return `${desc} [${val}]`;
    else return `[${val}]`;
  };

  handleRowClick = async (currentRow: RuleDefinition) => {
    if (this.state.isDirty) {
      if (
        window.confirm("You have unsaved changes.  Would you like to save?")
      ) {
        await this.postRuleDefinition();
      }
    }
    let categories: DropdownCategory[] = [];

    if (currentRow && currentRow.tableName) {
      const res = await Http.get<any>({
        url: "SystemSettings/GetCategoriesByTableName",
        data: { name: currentRow.tableName },
      });

      categories = res.categories;
      categories.unshift({ id: 0, description: "< Select >" });
    }
    //build the hash table only when changing rows
    const descriptionLkp: DescriptionHashTable = {};
    for (let cat of categories) {
      if (cat && cat.id) {
        descriptionLkp[cat.id] = cat.description;
      }
    }
    const newSelectedRule = {
      ...currentRow,
      description: currentRow.description || "",
    };
    this.setState({
      selectedRule: newSelectedRule,
      isDirty: false,
      possibleCategories: categories,
      descriptionLkp,
    });
  };

  ruleColumns: DataTableColumn<RuleDefinition>[] = [
    {
      Header: "Name",
      accessor: "name",
      width: 400,
    },
    {
      Header: "Contains",
      accessor: "tableName",
      width: 230,
    },
    {
      Header: "Description",
      accessor: "description",
    },
  ];

  ruleValueValidation = Yup.string()
    .required()
    .test(
      "numeric",
      "Please enter only positive numbers.",
      (val) => Number(val) > 0
    );

  render() {
    const { selectedRule } = this.state;

    let addValueField = (
      <TextBox
        label="Add a new value"
        name="newValue"
        value={this.state.newValue}
        key={this.state.selectedRule.name}
        readOnly={!this.state.selectedRule.id}
        onChange={(e: any) => this.setState(getInputValueByName(e))}
      />
    );

    if (selectedRule && this.state.possibleCategories.length > 1) {
      addValueField = (
        <Select
          label="Choose a new value"
          name="newValue"
          getName={(o) => o.description}
          options={this.state.possibleCategories}
          value={Number(this.state.newValue) || 0}
          onChange={(e: any) => this.addRuleValue(e.target.value)}
        />
      );
    }

    return (
      <BasePage>
        <Row>
          <Col xs={12}>
            <Panel title="Rule Settings">
              <div>
                <Row>
                  <Col sm={12} md={6}>
                    <TextBox
                      label="Name"
                      readOnly
                      name="name"
                      value={this.state.selectedRule.name}
                    />
                  </Col>
                  <Col sm={12} md={6}>
                    <TextBox
                      label="Value Types"
                      readOnly
                      name="tableName"
                      value={this.state.selectedRule.tableName}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12}>
                    <TextBox
                      multiline
                      rows={3}
                      label="Description"
                      readOnly
                      name="description"
                      value={this.state.selectedRule.description}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12}>
                    <div className="form-group">
                      <label className="control-label">Current Values</label>
                      <div
                        className="well"
                        style={{ padding: "6px", margin: "2px" }}
                      >
                        {this.state.selectedRule.ruleValues.map((rv) => (
                          <SelectTag data={rv} onClick={this.removeRuleValue}>
                            {this.getCategoryDescription(rv)}
                          </SelectTag>
                        ))}
                      </div>
                    </div>
                  </Col>
                </Row>
                <Form
                  name="addRuleValueForm"
                  values={this.state.newValue}
                  onSubmit={() => this.addRuleValue(this.state.newValue)}
                  validationSchema={this.ruleValueValidation}
                >
                  {(formProps: FormState) => (
                    <Row>
                      <Col sm={12} md={6}>
                        {addValueField}
                      </Col>
                      <Col sm={12} md={6}>
                        {!this.state.possibleCategories.length && (
                          <div
                            className="pull-right"
                            style={{ marginTop: "17px" }}
                          >
                            <Button
                              type="submit"
                              color="default"
                              disabled={!this.state.selectedRule.id}
                            >
                              Add New Custom Value
                            </Button>
                          </div>
                        )}
                      </Col>
                    </Row>
                  )}
                </Form>
                <Row>
                  <Col xs={12}>
                    <Button
                      type="button"
                      color="success"
                      disabled={
                        !this.state.selectedRule.id && this.state.isDirty
                      }
                      onClick={this.postRuleDefinition}
                    >
                      Save Changes
                    </Button>
                  </Col>
                </Row>
              </div>

              <div className="full-width">
                <DataTable
                  filterable
                  data={rules.all}
                  columns={this.ruleColumns}
                  isRowSelected={(row: RuleDefinition) =>
                    row.id === this.state.selectedRule.id
                  }
                  onRowClick={this.handleRowClick}
                />
              </div>
            </Panel>
          </Col>
        </Row>
      </BasePage>
    );
  }
}
